import React from "react";
import { Box, Button, Typography } from "@mui/material";

/**
 * Empty/null state (mobile) — the mobile equivalent of the desktop
 * `NoResultComponent` (components/Home/TodoList.jsx) and `NoAssessmentsFound`.
 *
 * Same anatomy as those: a centered graphic, a grey message, and an optional
 * "Clear filters" action. Desktop uses a remote illustration; the mobile Figma
 * uses a circular icon badge instead, so that's what this renders.
 *
 * `description` takes a node (not just a string) so callers can pass the same
 * two-line `<>line one<br/>line two</>` fragments the desktop copy uses.
 *
 * @param {React.ElementType} icon        MUI icon rendered inside the badge
 * @param {React.ReactNode}   description the message
 * @param {Function} [onClearFilters]     when set, renders the Clear filters button
 * @param {number}   [minHeight]          keeps the section from collapsing
 */
export default function NullState({
  icon: Icon,
  description,
  onClearFilters,
  minHeight = 220,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 3,
        minHeight,
        py: 6,
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            bgcolor: "primary.clr-50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 32, color: "primary.clr-200" }} />
        </Box>
      )}

      <Typography
        variant="body02-regular"
        color="neutral.clr-500"
        sx={{ maxWidth: 280, lineHeight: 1.6 }}
      >
        {description}
      </Typography>

      {onClearFilters && (
        <Button variant="contained" size="small" onClick={onClearFilters}>
          Clear filters
        </Button>
      )}
    </Box>
  );
}
